import { useEffect, useRef } from 'react';
import { FieldValues, UseFromSubscribe } from 'react-hook-form';
import { useDebounce } from './useDebounce';

interface Options {
  // 変更しても自動submitを実行させないフィールドの一覧
  ignoreAutoSubmitFields?: string[];
}

/**
 * 最後の入力から一定時間後にフォームをsubmitするためのフック
 * @param subscribe useFormのsubscribe関数
 * @param callback 一定時間後に実行する処理。通常は `() => handleSubmit(onSubmit)()` のようなコード。
 * @param options オプション
 */
export const useDebouncedSubmit = <TFieldValues extends FieldValues>(
  subscribe: UseFromSubscribe<TFieldValues>,
  callback: () => void,
  options: Options = {}
) => {
  const debouncedSubmit = useDebounce(callback, 1000);
  const optionsRef = useRef(options);

  useEffect(() => {
    const unsubscribe = subscribe({
      callback: ({ name, dirtyFields, isSubmitting }) => {
        const ignoreAutoSubmitFields = optionsRef.current?.ignoreAutoSubmitFields || [];
        // dirtyFields[name] が真であれば、変更通知されたフィールドが確かにdirty扱いされていることが確認できる
        // submit開始後〜isSubmittingがfalseに戻るまでの間、余計な変更通知が来るため無視する (おそらくuseFormに新しいvaluesが設定された時の副作用)
        if (name && dirtyFields && name in dirtyFields && !isSubmitting && !ignoreAutoSubmitFields.includes(name)) {
          debouncedSubmit();
        }
      }
    });
    return () => unsubscribe();
  }, [subscribe, debouncedSubmit]);
};
